import {
  computed,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { IFont } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { FontGateway } from './font.gateway';

@Injectable({
  providedIn: 'root',
})
export class FontStore {
  private readonly gateway: FontGateway = new FontGateway();

  readonly #fontResource: ResourceRef<IApiResponse<IFont[]> | undefined> =
    resource({
      loader: (): Promise<IApiResponse<IFont[]>> => {
        return firstValueFrom(this.gateway.list());
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#fontResource.isLoading()
  );
  readonly fonts: Signal<IFont[]> = computed((): IFont[] =>
    this.#fontResource.hasValue()
      ? (this.#fontResource.value()?.data ?? [])
      : []
  );

  loadFonts(): void {
    this.#fontResource.reload();
  }

  getFile(font: IFont): Promise<Blob> {
    return firstValueFrom(this.gateway.getFile(font));
  }

  updateFonts(query: IFont[]): void {
    this.#fontResource.update((): IApiResponse<IFont[]> => {
      return {
        data: query,
      };
    });
  }

  upload(query: FormData): Promise<IFont[]> {
    return firstValueFrom(this.gateway.upload(query)).then(
      (result: IApiResponse<IFont[]>): IFont[] => {
        if (result && result.data) {
          this.updateFonts(result.data);
          return result.data;
        }
        return [];
      }
    );
  }

  delete(query: IFont): Promise<void> {
    return firstValueFrom(this.gateway.delete(query)).then(
      (result: IApiResponse<IFont[]>): void => {
        if (result && result.data) {
          this.updateFonts(result.data);
        }
      }
    );
  }
}
